// EventPortalNavBar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import axios from 'axios';

/* ───────── STYLES (match MixerPortalNavBar) ───────── */
const S = {
  wrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  navRow: {
    height: 55,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative', // allows logo absolute centering
  },

  /* Exit button */
  exitBtn: {
    appearance: 'none',
    background: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    cursor: 'pointer',
    zIndex: 1,
  },

  /* Center logo */
  logoCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  logo: {
    height: 'auto',
    width: 72,
  },

  /* Occupancy pill */
  occPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '0px 10px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    zIndex: 1,
  },
  occIcon: {
    fontSize: 12,
    opacity: 0.85,
  },
};

/**
 * Sticky top-bar for **regular** event portal.
 * Props:
 *  - eventId (string, optional — will fallback to URL param)
 *  - event   (object, optional — when provided, passed to preview in router state)
 */
export default function EventPortalNavBar({ eventId, event }) {
  const [count, setCount] = useState(0);
  const navigate = useNavigate();
  const { eventId: routeEventId } = useParams();

  // Resolve eventId (prop > param); strip accidental "mixer-" just in case
  const resolvedEventId = String(eventId || routeEventId || '')
    .replace(/^mixer-/, '')
    .trim();

    useEffect(() => {
      if (!resolvedEventId) return;
    
      const fetchOccupancy = async () => {
        const url = `${process.env.REACT_APP_API_URL}/events/${resolvedEventId}?debug=1`;
        const headers = {
          Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          'X-Suppress-Global-Error': '1',
        };
        const t0 = Date.now();
        console.warn('[EventPortalNavBar] fetchOccupancy →', { url, resolvedEventId });
    
        try {
          const res = await axios.get(url, {
            headers,
            // Let us inspect 410 bodies instead of throwing
            validateStatus: (s) => [200, 401, 403, 404, 410].includes(s),
          });
    
          const elapsed = Date.now() - t0;
          console.warn('[EventPortalNavBar] fetchOccupancy ←', {
            status: res.status,
            elapsedMs: elapsed,
            reasonHdr: res.headers?.['x-event-ended-reason'] || null,
            endMsHdr : res.headers?.['x-event-endms'] || null,
            nowHdr   : res.headers?.['x-event-now'] || null,
          });
    
          if (res.status === 200) {
            const occ = Number(res.data?.occupancyCount ?? NaN);
            console.warn('[EventPortalNavBar] occupancy payload', {
              occupancyCount: res.data?.occupancyCount,
              parsed: occ,
            });
            if (Number.isFinite(occ)) setCount(occ);
          } else if (res.status === 410) {
            console.warn('[EventPortalNavBar] 410 on occupancy', {
              reason: res.data?.reason || '(no reason in body)',
              debug : res.data?.debug || null,
            });
            // do nothing (best-effort poll)
          } else {
            console.warn('[EventPortalNavBar] non-200/410 occupancy', {
              status: res.status,
              bodyKeys: res.data ? Object.keys(res.data) : [],
            });
          }
        } catch (err) {
          console.error('[EventPortalNavBar] fetchOccupancy error', {
            message: err?.message,
            status : err?.response?.status,
          });
        }
      };
    
      fetchOccupancy();
      const id = setInterval(fetchOccupancy, 5000);
      return () => clearInterval(id);
    }, [resolvedEventId]);
    
    const handleExit = () => {
      console.warn('[EventPortalNavBar] Exit clicked', {
        resolvedEventId,
        hasEventInState: !!event,
      });
    
      if (!resolvedEventId) {
        console.warn('[EventPortalNavBar] Exit aborted (no resolvedEventId)');
        return;
      }
    
      const navState = event ? { state: { event, fromExit: true } } : undefined;
      console.warn('[EventPortalNavBar] navigate →', {
        path: `/events/${resolvedEventId}`,
        withState: !!navState,
      });
    
      navigate(`/events/${resolvedEventId}`, navState);
    };
    

  return (
    <div style={S.wrapper}>
      <div style={S.navRow}>
        {/* Left: Exit button */}
        <button style={S.exitBtn} onClick={handleExit}>
          Exit
        </button>

        {/* Center: Tribe Logo */}
        <div style={S.logoCenter}>
          <TribeLogo style={S.logo} />
        </div>

        {/* Right: Occupancy */}
        <div style={S.occPill} aria-label="Occupancy">
          <FaUsers style={S.occIcon} />
          <span>{count}</span>
        </div>
      </div>
    </div>
  );
}
